import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';
import { useUserStore } from '@/store/userStore';
import { AI_ATTACHMENT_TOO_LARGE_FOR_SERVER, isOversizeSendFailure } from '@/utils/aiChatAttachments';
import {
  AiConversationError,
  activeConversationKey,
  workspaceModeKey,
  getConversation,
  type AiConversationsApiOptions,
  type AiComponentPayload,
} from '@/services/aiConversations';

/**
 * Client for the streamed AI chat endpoint.
 *
 * Contract (verified live against the branch API 2026-07-27):
 *   POST {api}/api/ai/chat
 *     JSON { message, conversation_id?: int, history?: [{role, text}] }
 *     Sanctum bearer auth like every other plain-HTTP call (certificate download
 *     pattern: VITE_BACKEND_BASE_URL minus the /graphql suffix + X-Current-School-ID).
 *   Response: SSE with named events
 *     event: conversation data: {"id": 12, "title": "..."}   — ALWAYS first
 *     event: delta        data: {"text": "..."}
 *     event: tool         data: {"name": "...", "status": "running"|"done"|"error"}
 *     event: done         data: {"finish": "stop"|"length"|"error"}
 *     event: error        data: {"message": "..."}
 *
 * Persistence is server-side. Once a conversation id is adopted (from the first
 * event, or by loading a stored one) every later turn sends `conversation_id`
 * and NO `history` — the server rebuilds context from stored turns and ignores
 * any history we send. `history` is legacy and only seeds a brand-new chat.
 */

/** Attachment metadata shown on a transcript bubble ({name, mime, size}). */
export interface AiChatAttachmentMeta {
  name: string;
  mime: string;
  size: number;
}

/** An attachment being SENT with a turn — metadata plus the base64 payload. */
export interface AiChatAttachmentUpload extends AiChatAttachmentMeta {
  data: string;
}

export interface AiChatMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Tool chips for this answer — replayed from stored `tool_activity` on resume. */
  toolActivity?: AiToolActivity[];
  /** What was attached to this turn — metadata only, never bytes. */
  attachments?: AiChatAttachmentMeta[];
}

/** A component the model chose to display in the workspace (full page + floating panel). */
export interface AiWorkspaceCardData {
  id: string;
  type: string;
  props: Record<string, unknown>;
  title?: string | null;
  /** User-triggered quick actions, pre-filtered by permission server-side. */
  actions?: Array<{ key: string; label: string; icon?: string | null }>;
}

/**
 * Whether new cards accumulate or replace the previous one. Paul: "should be
 * determined by the user for now" — so this is a user preference, persisted.
 */
export type AiWorkspaceMode = 'stack' | 'replace';

export type AiToolStatus = 'running' | 'done' | 'error';

export interface AiToolActivity {
  name: string;
  label: string;
  status: AiToolStatus;
}

export type AiFinishReason = 'stop' | 'length' | 'error';

/**
 * Incremental parser for the named-event SSE framing above. Feed it decoded
 * chunks (any split points, including mid-line); it dispatches once per blank
 * line per the SSE spec. Exported for direct testing.
 */
export function createSseParser(onEvent: (event: string, data: string) => void) {
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];

  const dispatch = () => {
    if (eventName || dataLines.length > 0) {
      onEvent(eventName || 'message', dataLines.join('\n'));
    }
    eventName = '';
    dataLines = [];
  };

  const handleLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return; // comment / keep-alive
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
    // id/retry/unknown fields are irrelevant to this consumer.
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      let newline: number;
      // Handle \n, \r\n and \r line endings without splitting a trailing partial line.
      while ((newline = buffer.search(/\r\n|\n|\r/)) !== -1) {
        const line = buffer.slice(0, newline);
        buffer = buffer.slice(newline + (buffer.startsWith('\r\n', newline) ? 2 : 1));
        handleLine(line);
      }
    },
    /** Flush a final un-terminated event when the stream closes. */
    flush() {
      if (buffer !== '') {
        handleLine(buffer);
        buffer = '';
      }
      dispatch();
    },
  };
}

const TOOL_VERBS: Record<string, string> = {
  search: 'Searching',
  get: 'Fetching',
  list: 'Listing',
  find: 'Finding',
  create: 'Creating',
  update: 'Updating',
  check: 'Checking',
};

/** Tools whose name does not read well through the verb rule below. */
const TOOL_LABELS: Record<string, string> = {
  get_whats_new: 'Checking what\'s new…',
};

/** "search_students" -> "Searching students…" (activity-chip labels, E2). */
export function humanizeToolName(name: string): string {
  const label = TOOL_LABELS[name.trim()];
  if (label) return label;
  const words = name.trim().split(/[_\-\s]+/).filter(Boolean);
  const first = words[0];
  if (!first) return 'Working…';
  const verb = TOOL_VERBS[first.toLowerCase()];
  const rest = words.slice(1).join(' ').replace(/_/g, ' ');
  if (verb) return rest ? `${verb} ${rest}…` : `${verb}…`;
  const phrase = words.join(' ');
  return `Running ${phrase}…`;
}

function defaultEndpoint(): string {
  const base = (import.meta.env.VITE_BACKEND_BASE_URL as string | undefined) ?? '';
  return `${base.replace(/\/graphql\/?$/, '')}/api/ai/chat`;
}

export interface UseAiChatOptions extends AiConversationsApiOptions {
  /** Test seam / override; defaults to VITE_BACKEND_BASE_URL minus /graphql + /api/ai/chat. */
  endpoint?: string;
  /** Test seam; defaults to global fetch. */
  fetchFn?: typeof fetch;
  /** Called whenever a conversation is created/adopted — hosts refresh their list. */
  onConversationChange?: (id: number | null) => void;
}

/** Map a stored `tool_activity` entry onto the live chip shape. */
function toolActivityFromStored(
  stored: Array<{ name: string; status?: string }> | null | undefined,
): AiToolActivity[] | undefined {
  if (!Array.isArray(stored) || stored.length === 0) return undefined;
  return stored
    .filter((t) => t && typeof t.name === 'string' && t.name)
    .map((t) => ({
      name: t.name,
      label: humanizeToolName(t.name),
      status: (t.status === 'running' || t.status === 'error' ? t.status : 'done') as AiToolStatus,
    }));
}

export function useAiChat(options: UseAiChatOptions = {}) {
  const messages = ref<AiChatMessage[]>([]);
  const streamingText = ref('');
  const toolActivity = ref<AiToolActivity[]>([]);
  const isStreaming = ref(false);
  const error = ref<string | null>(null);
  const finishReason = ref<AiFinishReason | null>(null);

  /** Components the model chose to display, newest first (workspace on both hosts). */
  const workspaceCards = ref<AiWorkspaceCardData[]>([]);
  const workspaceMode = ref<AiWorkspaceMode>('stack');

  /** Server-side conversation currently being continued (null = brand-new chat). */
  const conversationId = ref<number | null>(null);
  const conversationTitle = ref<string | null>(null);
  /** Inline, non-fatal message for load failures — the composer stays usable. */
  const loadError = ref<string | null>(null);
  const isLoadingConversation = ref(false);

  let controller: AbortController | null = null;

  // Storage key is derived per user+school. The store may be unavailable in
  // isolated unit tests (no active pinia) — fall back to an anonymous key.
  const storageKey = () => {
    let userId: string | number | null = null;
    try {
      userId = (useUserStore().currentUser as { id?: string | number } | null)?.id ?? null;
    } catch {
      /* pinia not active — anonymous scope */
    }
    return activeConversationKey(userId);
  };

  const persistConversationId = (id: number | null) => {
    try {
      if (id === null || id === undefined) localStorage.removeItem(storageKey());
      else localStorage.setItem(storageKey(), String(id));
    } catch {
      /* storage unavailable (private mode) — resume simply won't persist */
    }
  };

  const adoptConversation = (id: number | null, title?: string | null) => {
    conversationId.value = id;
    if (title !== undefined) conversationTitle.value = title;
    persistConversationId(id);
    options.onConversationChange?.(id);
  };

  // --- workspace cards -----------------------------------------------------

  const modeStorageKey = () => {
    let userId: string | number | null = null;
    try {
      userId = (useUserStore().currentUser as { id?: string | number } | null)?.id ?? null;
    } catch {
      /* pinia not active — anonymous scope */
    }
    return workspaceModeKey(userId);
  };

  // Restore the user's stack/replace preference up front.
  try {
    const storedMode = localStorage.getItem(modeStorageKey());
    if (storedMode === 'stack' || storedMode === 'replace') workspaceMode.value = storedMode;
  } catch {
    /* storage unavailable — fall back to the default */
  }

  const setWorkspaceMode = (mode: AiWorkspaceMode) => {
    workspaceMode.value = mode;
    try {
      localStorage.setItem(modeStorageKey(), mode);
    } catch {
      /* storage unavailable — preference just won't persist */
    }
    // Switching to replace collapses the stack to the newest card.
    if (mode === 'replace' && workspaceCards.value.length > 1) {
      workspaceCards.value = workspaceCards.value.slice(0, 1);
    }
  };

  /** Normalise a payload from the SSE event or from a stored message. */
  const toCard = (raw: unknown): AiWorkspaceCardData | null => {
    const payload = raw as Partial<AiComponentPayload> | null;
    if (!payload || typeof payload.type !== 'string' || !payload.type) return null;
    const id = typeof payload.id === 'string' && payload.id
      ? payload.id
      : `${payload.type}:${JSON.stringify(payload.props ?? {})}`;
    // `actions` is pre-filtered by permission server-side; keep only
    // well-formed entries. Unknown keys are dropped later, at render time.
    const actions = Array.isArray(payload.actions)
      ? payload.actions
        .filter((a): a is { key: string; label: string; icon?: string | null } =>
          !!a && typeof a.key === 'string' && !!a.key)
        .map((a) => ({
          key: a.key,
          label: typeof a.label === 'string' && a.label ? a.label : a.key,
          icon: typeof a.icon === 'string' ? a.icon : null,
        }))
      : [];

    return {
      id,
      type: payload.type,
      props: (payload.props ?? {}) as Record<string, unknown>,
      title: typeof payload.title === 'string' ? payload.title : null,
      actions,
    };
  };

  const addWorkspaceCard = (card: AiWorkspaceCardData) => {
    if (workspaceMode.value === 'replace') {
      workspaceCards.value = [card];
      return;
    }
    // Stack: newest first, and re-displaying the same card moves it to the top.
    workspaceCards.value = [card, ...workspaceCards.value.filter((c) => c.id !== card.id)];
  };

  const dismissWorkspaceCard = (id: string) => {
    workspaceCards.value = workspaceCards.value.filter((c) => c.id !== id);
  };

  const clearWorkspaceCards = () => {
    workspaceCards.value = [];
  };

  const handleEvent = (event: string, data: string) => {
    let payload: Record<string, unknown> = {};
    try {
      payload = data ? (JSON.parse(data) as Record<string, unknown>) : {};
    } catch {
      return; // malformed frame — skip rather than blank-screen (E2)
    }

    if (event === 'conversation') {
      // Always the first event of a stream — adopt the id so the NEXT turn
      // continues this chat instead of forking a new one.
      const id = typeof payload.id === 'number' ? payload.id : Number(payload.id);
      if (Number.isFinite(id) && id > 0) {
        adoptConversation(id, typeof payload.title === 'string' ? payload.title : null);
      }
    } else if (event === 'component') {
      // The MODEL decides what to display; we only render registered types.
      const card = toCard(payload);
      if (card) addWorkspaceCard(card);
    } else if (event === 'delta') {
      if (typeof payload.text === 'string') streamingText.value += payload.text;
    } else if (event === 'tool') {
      const name = typeof payload.name === 'string' ? payload.name : '';
      if (!name) return;
      const status = (payload.status as AiToolStatus) ?? 'running';
      const existing = [...toolActivity.value];
      // Update the most recent chip for this tool, or append a new one.
      const idx = existing.map((t) => t.name).lastIndexOf(name);
      const current = idx === -1 ? undefined : existing[idx];
      if (current && current.status === 'running') {
        existing[idx] = { ...current, status };
      } else {
        existing.push({ name, label: humanizeToolName(name), status });
      }
      toolActivity.value = existing;
    } else if (event === 'done') {
      const finish = payload.finish;
      finishReason.value =
        finish === 'stop' || finish === 'length' || finish === 'error' ? finish : 'stop';
    } else if (event === 'error') {
      error.value =
        typeof payload.message === 'string' && payload.message
          ? payload.message
          : 'The assistant ran into a problem. Please try again.';
    }
  };

  /** Fold any partially-streamed answer into the transcript and reset stream state. */
  const finalize = () => {
    if (streamingText.value) {
      // Keep the chips with the answer so a live turn looks like a resumed one.
      const chips = toolActivity.value.map((t) => ({
        ...t,
        status: t.status === 'running' ? ('done' as const) : t.status,
      }));
      messages.value = [
        ...messages.value,
        {
          role: 'assistant',
          text: streamingText.value,
          ...(chips.length ? { toolActivity: chips } : {}),
        },
      ];
    }
    streamingText.value = '';
    isStreaming.value = false;
    // A stream that ended while chips still say "running" is over either way.
    toolActivity.value = toolActivity.value.map((t) =>
      t.status === 'running' ? { ...t, status: 'done' as const } : t,
    );
    controller = null;
  };

  /** One POST attempt. Returns 'stale' when the server rejects our conversation_id. */
  const streamTurn = async(
    message: string,
    useConversationId: boolean,
    attachments: AiChatAttachmentUpload[] = [],
  ): Promise<'ok' | 'stale'> => {
    const doFetch = options.fetchFn ?? fetch;
    const endpoint = options.endpoint ?? defaultEndpoint();
    const token = localStorage.getItem(LocalStorageKeys.token);
    const schoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId);

    const activeId = useConversationId ? conversationId.value : null;
    // Once a conversation is adopted the server owns the transcript: send the
    // id and NO history. `history` only seeds a brand-new conversation.
    const body: Record<string, unknown> = { message };
    if (attachments.length > 0) {
      // The bytes ride this ONE turn; the server stores metadata only.
      body.attachments = attachments.map((a) => ({ name: a.name, mime: a.mime, data: a.data }));
    }
    if (activeId !== null) {
      body.conversation_id = activeId;
    } else {
      body.history = messages.value
        .slice(0, -1) // drop the just-appended user turn — it goes as `message`
        .map((m) => ({ role: m.role, text: m.text }));
    }

    const res = await doFetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(schoolId ? { 'X-Current-School-ID': schoolId } : {}),
      },
      body: JSON.stringify(body),
      signal: controller?.signal,
    });

    if (!res.ok) {
      // A stale/foreign conversation_id 404s — start fresh rather than alarm the user.
      if (res.status === 404 && activeId !== null) {
        adoptConversation(null, null);
        return 'stale';
      }
      // A proxy refusing an oversize body answers 413, often as HTML.
      if (attachments.length > 0 && isOversizeSendFailure(null, res.status)) {
        throw new Error(AI_ATTACHMENT_TOO_LARGE_FOR_SERVER);
      }
      // 403 (gating matrix) and 503 (unconfigured) arrive as JSON {message}.
      let detail = `The AI assistant is unavailable right now (HTTP ${res.status}).`;
      try {
        const errBody = (await res.json()) as { message?: string };
        if (errBody?.message) detail = errBody.message;
      } catch {
        /* non-JSON error body — keep the generic message */
      }
      throw new Error(detail);
    }

    if (!res.body) throw new Error('Streaming responses are not supported in this browser.');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const parser = createSseParser(handleEvent);
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode()); // drain any buffered multi-byte tail
    parser.flush();
    return 'ok';
  };

  const send = async(text: string, attachments: AiChatAttachmentUpload[] = []) => {
    const message = text.trim();
    if (!message || isStreaming.value) return;

    error.value = null;
    loadError.value = null;
    finishReason.value = null;
    toolActivity.value = [];

    messages.value = [...messages.value, {
      role: 'user',
      text: message,
      ...(attachments.length > 0
        ? { attachments: attachments.map(({ name, mime, size }) => ({ name, mime, size })) }
        : {}),
    }];

    isStreaming.value = true;
    streamingText.value = '';
    controller = new AbortController();

    try {
      const outcome = await streamTurn(message, true, attachments);
      // Stored conversation was gone — retry once as a brand-new chat.
      if (outcome === 'stale') await streamTurn(message, false, attachments);
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') {
        // A proxy that rejects the body without CORS headers surfaces only as
        // "Failed to fetch"; with files attached, size is the likely cause.
        if (attachments.length > 0 && isOversizeSendFailure(e)) {
          error.value = AI_ATTACHMENT_TOO_LARGE_FOR_SERVER;
        } else {
          error.value = e instanceof Error && e.message
            ? e.message
            : 'The assistant ran into a problem. Please try again.';
        }
      }
      // Abort keeps whatever streamed so far — finalize() folds it in below.
    } finally {
      finalize();
    }
  };

  const abort = () => {
    controller?.abort();
  };

  /** Clear the transcript and forget the active conversation (New chat). */
  const startNewChat = () => {
    abort();
    messages.value = [];
    streamingText.value = '';
    toolActivity.value = [];
    error.value = null;
    loadError.value = null;
    finishReason.value = null;
    conversationTitle.value = null;
    clearWorkspaceCards();
    adoptConversation(null, null);
  };

  /**
   * Load a stored conversation and continue it. Aborts any in-flight stream
   * first so switching mid-answer can't interleave two transcripts.
   * A 404 (stale/foreign id) silently falls back to a fresh chat.
   */
  const loadConversation = async(id: number): Promise<boolean> => {
    abort();
    loadError.value = null;
    error.value = null;
    isLoadingConversation.value = true;
    try {
      const detail = await getConversation(id, options);
      if (!detail) {
        adoptConversation(null, null);
        return false;
      }
      messages.value = (detail.messages ?? [])
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => {
          const chips = toolActivityFromStored(m.tool_activity);
          const attachments = Array.isArray(m.attachments)
            ? (m.attachments as AiChatAttachmentMeta[]).filter((a) => a && typeof a.name === 'string')
            : [];
          return {
            role: m.role,
            text: m.text ?? '',
            ...(chips ? { toolActivity: chips } : {}),
            ...(attachments.length > 0 ? { attachments } : {}),
          };
        });
      // Rebuild the workspace from components persisted on assistant messages.
      // Messages arrive oldest-first; reverse so the newest card ends up on top.
      const restored = (detail.messages ?? [])
        .flatMap((m) => (Array.isArray(m.components) ? m.components : []))
        .map(toCard)
        .filter((c): c is AiWorkspaceCardData => c !== null)
        .reverse();
      // De-dupe by id, keeping the newest occurrence.
      const seen = new Set<string>();
      const deduped = restored.filter((c) => (seen.has(c.id) ? false : seen.add(c.id)));
      workspaceCards.value = workspaceMode.value === 'replace' ? deduped.slice(0, 1) : deduped;

      streamingText.value = '';
      toolActivity.value = [];
      finishReason.value = null;
      adoptConversation(detail.id, detail.title ?? null);
      return true;
    } catch (e) {
      if (e instanceof AiConversationError && e.status === 404) {
        // Gone or not ours — drop it and start fresh, no scary error.
        messages.value = [];
        conversationTitle.value = null;
        adoptConversation(null, null);
        return false;
      }
      loadError.value = e instanceof Error && e.message
        ? e.message
        : 'Could not load that conversation.';
      return false;
    } finally {
      isLoadingConversation.value = false;
    }
  };

  /**
   * Restore the conversation this user+school was last using. Called when the
   * panel opens and on page load, so a reload resumes instead of starting over.
   */
  const resumeStoredConversation = async(): Promise<boolean> => {
    if (conversationId.value !== null || messages.value.length > 0) return false;
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(storageKey());
    } catch {
      return false;
    }
    const id = Number(stored);
    if (!stored || !Number.isFinite(id) || id <= 0) return false;
    return loadConversation(id);
  };

  const reset = () => {
    startNewChat();
  };

  return {
    messages,
    streamingText,
    toolActivity,
    isStreaming,
    error,
    finishReason,
    conversationId,
    conversationTitle,
    loadError,
    isLoadingConversation,
    workspaceCards,
    workspaceMode,
    setWorkspaceMode,
    addWorkspaceCard,
    dismissWorkspaceCard,
    clearWorkspaceCards,
    send,
    abort,
    reset,
    startNewChat,
    loadConversation,
    resumeStoredConversation,
  };
}

/**
 * The chat state/api surface hosts hand to AiAssistantChatPanel. Kept as a
 * named type so the panel and future hosts (full-page rework, conversation
 * persistence) share one contract.
 */
export type UseAiChatReturn = ReturnType<typeof useAiChat>;
